import React from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PendingApprovalDashboard from '../components/PendingApprovalDashboard';
import { User, AlertTriangle } from 'lucide-react';

// Protected Route Component
export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check if user has pending approval status
  if (user?.approval_status === 'pending') {
    return <PendingApprovalDashboard />;
  }

  // Check if user is disabled
  if (user?.is_disabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8">
            <div className="mx-auto h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <User className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-secondary-900 mb-2">Account Disabled</h2>
            <p className="text-secondary-600 mb-4">
              Your account has been disabled by an administrator. Please contact support for assistance.
            </p>
            <button
              onClick={() => {
                localStorage.removeItem('token');
                delete axios.defaults.headers.common['Authorization'];
                window.location.href = '/login';
              }}
              className="btn-primary"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user needs to complete onboarding
  // If onboarding_completed is true, user can access dashboard regardless of 2FA/GitHub status
  const needsOnboarding = !user?.onboarding_completed;
  
  // If accessing dashboard and needs onboarding, redirect to onboarding
  if (needsOnboarding && window.location.pathname === '/dashboard') {
    return <Navigate to="/onboarding" />;
  }

  return children;
};

// Role-based route protection component
export const RoleProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Check if user has pending approval status
  if (user?.approval_status === 'pending') {
    return <PendingApprovalDashboard />;
  }

  // Check if user is disabled
  if (user?.is_disabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8">
            <div className="mx-auto h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <User className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-secondary-900 mb-2">Account Disabled</h2>
            <p className="text-secondary-600 mb-4">
              Your account has been disabled by an administrator. Please contact support for assistance.
            </p>
            <button
              onClick={() => {
                localStorage.removeItem('token');
                delete axios.defaults.headers.common['Authorization'];
                window.location.href = '/login';
              }}
              className="btn-primary"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check role-based access
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8">
            <div className="mx-auto h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-secondary-900 mb-2">Access Denied</h2>
            <p className="text-secondary-600 mb-4">
              You don't have permission to access this page. This area is restricted to {allowedRoles.join(' or ')} users only.
            </p>
            <button
              onClick={() => window.history.back()}
              className="btn-primary"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

// Special route protection for pending approval page
export const PendingApprovalRoute = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If user doesn't have pending status, redirect to appropriate page
  if (user?.approval_status !== 'pending') {
    if (user?.is_disabled) {
      return <Navigate to="/login" />;
    }
    return <Navigate to="/dashboard" />;
  }

  // Only show pending approval dashboard if user is authenticated and has pending status
  return <PendingApprovalDashboard />;
};
